import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import CustomTextField from "../common/CustomTextField";
import CustomPasswordTextField from "../common/CustomPasswordTextField";
import { LockOutlined } from "@mui/icons-material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { useForm } from "react-hook-form";
import { useModalStyles } from "../common/useModalStyles";
import {
  LoginInfo,
  loginInfoInitialValues,
  relogin,
  resetAuthState,
  resetReloginFetchStatus,
  useAuthSlice,
} from "../../redux/slice/authSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { useNavigation } from "../../hooks/useNavigation";

interface ReSignInModalProps {
  open: boolean;
  handleClose: () => void;
}

const COUNTDOWN_START = 30;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const ReSignIn: React.FC<ReSignInModalProps> = ({ open, handleClose }) => {
  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    inputFieldSx,
    primaryBtnSx,
  } = useModalStyles();

  const dispatch          = useAppDispatch();
  const { navigate }      = useNavigation();
  const { reLoginStatus } = useAuthSlice();

  const { control, handleSubmit } = useForm<LoginInfo>({
    defaultValues: loginInfoInitialValues,
  });

  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── helpers ───────────────────────────────────────────────────────────────
  const clearTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const hidePopup = useCallback(() => {
    clearTimer();
    handleClose();
  }, [handleClose]);

  const cancelPopup = useCallback(() => {
    clearTimer();
    handleClose();
    dispatch(resetAuthState());
    navigate("/signIn");
  }, [handleClose, dispatch, navigate]);

  // ── relogin success ───────────────────────────────────────────────────────
  useEffect(() => {
    if (open && reLoginStatus === "success") {
      hidePopup();
      dispatch(resetReloginFetchStatus());
    }
  }, [open, reLoginStatus]);

  // ── countdown ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setCountdown(COUNTDOWN_START);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { cancelPopup(); return prev; }
        return prev - 1;
      });
    }, 1000);
    return clearTimer;
  }, [open]);

  const onSubmit = useCallback(async (data: LoginInfo) => {
    await dispatch(relogin(data)).unwrap();
  }, []);

  const progressPct = (countdown / COUNTDOWN_START) * 100;
  const progressColor =
    progressPct > 50 ? tokens.accent :
    progressPct > 20 ? "#f59e0b"     : "#ef4444";

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={cancelPopup}
      onClick={(e) => e.stopPropagation()}
      maxWidth={false}
      PaperProps={{
        elevation: 0,
        sx: { ...paperSx(), width: 400 },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.75)" : "rgba(20,23,30,0.45)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", flex: 1 }}>

        <Box sx={{ height: 3, background: tokens.border, flexShrink: 0 }}>
          <Box sx={{
            height: "100%",
            width: `${progressPct}%`,
            background: progressColor,
            transition: "width 1s linear, background 0.5s ease",
          }} />
        </Box>

        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <LockOutlined sx={{ fontSize: 20, color: tokens.accent }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography sx={{
              fontSize: "0.95rem", fontWeight: 700,
              color: tokens.textPrimary, lineHeight: 1.25, mb: 0.5,
            }}>
              Session expired
            </Typography>
            <Typography sx={{
              fontSize: "0.78rem", color: tokens.textSecondary, lineHeight: 1.6,
            }}>
              For your security, please verify your identity
              to continue where you left off.
            </Typography>
          </Box>

          <IconButton size="small" onClick={cancelPopup} sx={closeIconSx}>
            <GridCloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        <Box sx={{ ...bodySx, gap: 2 }}>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textPrimary }}>
              Email
            </Typography>
            <CustomTextField
              controllerName="userName"
              control={control}
              size="small"
              placeholder="Enter your email"
              rules={{ required: "Email is required." }}
              sx={inputFieldSx()}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textPrimary }}>
              Password
            </Typography>
            <CustomPasswordTextField
              controllerName="password"
              control={control}
              size="small"
              fullWidth
              placeholder="Enter your password"
              autoComplete="current-password"
              rules={{ required: "Password is required." }}
              sx={inputFieldSx()}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ flex: 1, height: "1px", background: tokens.border }} />
            <Typography sx={{ fontSize: "0.68rem", color: tokens.textSecondary, whiteSpace: "nowrap" }}>
              session timer
            </Typography>
            <Box sx={{ flex: 1, height: "1px", background: tokens.border }} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary }}>
              Signing you out automatically in
            </Typography>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75,
              px: 1.25, py: 0.5, borderRadius: "20px",
              background: isDark ? "rgba(245,158,11,0.10)" : "#fff8ec",
              border: `1px solid ${isDark ? "rgba(245,158,11,0.2)" : "#ffe0a0"}`,
            }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: "50%",
                background: "#f59e0b", flexShrink: 0,
              }} />
              <Typography sx={{
                fontSize: "0.72rem", fontWeight: 700,
                color: isDark ? "#fcd34d" : "#92400e",
              }}>
                {countdown}s
              </Typography>
            </Box>
          </Box>

        </Box>

        <Box sx={{ ...footerSx, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={primaryBtnSx}
          >
            Continue session
          </Button>

          <Typography sx={{
            textAlign: "center", fontSize: "0.7rem",
            color: tokens.textSecondary,
          }}>
            Having trouble? Contact your system administrator
          </Typography>
        </Box>

      </form>
    </Dialog>
  );
};

export default ReSignIn;